import { Alert } from '@ksyos/design-system';
import { useSelector } from 'react-redux';
import type {
    ITocV1DiagnosticReportCustomControlDataBevindingen,
    ITocV1DiagnosticReportCustomControlDataWerkdiagnose,
} from '@common/validation/customControls/ophthalmology';
import type { IReduxState } from '@frontend/configureStore';
import { ConclusieDiagnoseOption, ReportStatus } from '@common/enums/';
import { getDisplayName } from '@common/utils/getDisplayName';
import {
    OphthalmologyPoliciesEnum,
    UrgenciesForConsultOogartsEnum,
} from '@common/validation/customControls/ophthalmology';
import { translateKey } from '@frontend/translate';
import type { ICustomControlDiagnosticReportReadProps } from '../../interfaces';
import { DiagnosticReportReadButtons } from '../../../../elements/carePathButtons/DiagnosticReportReadButtons';
import { FormDivider, FormParagraph, HeaderDivider } from '../../../../elements/common';
import { FlexColumns } from '../../../../elements/FlexColumns';
import { FormLabel, HorizontalFormGroupRead, InputContainer } from '../../../../elements/forms';
import { ReadCustomControlData } from '../../../../elements/forms/ReadCustomControlData';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { RequestMessageContainer } from '../../../episodes/RequestMessageContainer';
import { TooV1SupervisionReadDraft } from '../../TOO/v1/TooV1SupervisionReadDraft';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { Icd10SelectionsRead } from '../../utils/v1/Icd10/Icd10SelectionsRead';
import { tocV1DiagnosticReportWerkdiagnoseLabels } from './TocV1DiagnosticReportAddEdit';

const bevindingenLabels: { [key: string]: string } = {
    niet_te_beoordelen: 'Niet te beoordelen',
    geen_afwijkingen: 'Geen afwijkingen',
    bloedinkjes: 'Bloedinkjes',
};

export function TocV1DiagnosticReportRead(props: ICustomControlDiagnosticReportReadProps) {
    const beleid =
        props.diagnosticReport.customControlData.beleid !== OphthalmologyPoliciesEnum.anders
            ? props.diagnosticReport.customControlData.beleid
            : props.diagnosticReport.customControlData.beleid_anders;

    const urgentie =
        props.diagnosticReport.customControlData.urgentie !== UrgenciesForConsultOogartsEnum.anders
            ? props.diagnosticReport.customControlData.urgentie
            : props.diagnosticReport.customControlData.urgentie_anders;

    const isDraft = props.diagnosticReport.reviewStatus === ReportStatus.draft;

    const practitionerId = useSelector((state: IReduxState) => state.authentication.id);
    const organizationId = useSelector((state: IReduxState) => state.authentication.organizationId);

    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                date={props.diagnosticReport && props.diagnosticReport.dateSent}
                isFirstItem={props.isFirstItem}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) =>
                    await props.updateItemState({
                        ...props.itemState,
                        isOpen: isOpened || props.itemState.initialIsOpen === true,
                    })
                }
                organizationName={props.diagnosticReport.performerOrganization?.name}
                practitionerName={
                    props.diagnosticReport.performerPractitioner
                        ? getDisplayName(props.diagnosticReport.performerPractitioner)
                        : undefined
                }
                process={props.process}
                title={translateKey('diagnosticReport.category_consultReport')}
            />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                <HorizontalFormGroupRead>
                    <FormLabel>Bevindingen</FormLabel>
                    <InputContainer>
                        {bevindingenOption('niet_te_beoordelen')}
                        {bevindingenOption('geen_afwijkingen')}
                        {bevindingenOption('bloedinkjes')}
                        {bevindingenOption('andersText')}
                    </InputContainer>
                </HorizontalFormGroupRead>

                <HorizontalFormGroupRead>
                    <FormLabel>Werkdiagnose</FormLabel>
                    <InputContainer>
                        {props.diagnosticReport.customControlData.heb_je_een_diagnose !==
                            ConclusieDiagnoseOption.ja && (
                            <FormParagraph bold>
                                {props.diagnosticReport.customControlData.heb_je_een_diagnose}
                            </FormParagraph>
                        )}
                        {werkdiagnoseOption('cataract')}
                        {werkdiagnoseOption('glaucoom', ['glaucoom_ja', 'glaucoom_nee', 'glaucoom_suspect'])}
                        {werkdiagnoseOption('macula_degeneratie', ['macula_nat', 'macula_droog', 'macula_suspect'])}
                        {werkdiagnoseOption('retinopathie', ['diabetische_retinopathie', 'retinopathie_overig'])}
                        {werkdiagnoseOption('droge_ogen')}
                    </InputContainer>
                </HorizontalFormGroupRead>

                <Icd10SelectionsRead
                    diagnosis={props.diagnosticReport.diagnosis}
                    hideTitle
                    hideWorkingDiagnosisLabel={isWerkdiagnoseVisible()}
                    showWorkingDiagnosis
                    thesaurusTermDate={props.diagnosticReport.dateSent}
                />

                {(props.diagnosticReport.customControlData.antwoord || props.diagnosticReport.remark) && (
                    <FormDivider />
                )}

                <ReadCustomControlData
                    label="Antwoord"
                    value={props.diagnosticReport.customControlData.antwoord}
                />

                <FormDivider />

                <HeaderDivider text="Advies" />

                <ReadCustomControlData
                    label="Beleid"
                    value={beleid}
                />
                <ReadCustomControlData
                    label="Urgentie"
                    value={urgentie}
                />
                <ReadCustomControlData
                    label="Bezoek aan specialist nu nodig?"
                    value={props.diagnosticReport.customControlData.bezoek_aan_specialist_nu_nodig}
                />
                <ReadCustomControlData
                    label="Toelichting"
                    value={props.diagnosticReport.remark}
                />

                {props.process.requestMessages && props.process.requestMessages.length > 0 && (
                    <RequestMessageContainer requestMessages={props.process.requestMessages} />
                )}
                {isDraft && <TooV1SupervisionReadDraft {...props} />}

                {props.diagnosticReport.customControlData.hasSupervisionFields &&
                    props.process.requesterOrganizationId === organizationId &&
                    props.process.requesterPractitionerId === practitionerId && (
                        <Alert appearance="info">{translateKey('tooSupervision.supervisionAlert')}</Alert>
                    )}

                <DiagnosticReportReadButtons
                    {...props}
                    requestMessageToGP
                />
            </CustomControlCollapseBody>

            {!props.itemState.initialIsOpen && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('diagnosticReport.hideConsultReport')}
                    labelMore={translateKey('diagnosticReport.showConsultReport')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}
        </ProcessContainer>
    );

    function bevindingenOption(name: keyof ITocV1DiagnosticReportCustomControlDataBevindingen) {
        const bevindingen = props.diagnosticReport.customControlData.bevindingen;
        const nameOd = name + '_od';
        const nameOs = name + '_os';

        return bevindingen[name] ? (
            <div className="row">
                <div className="col-sm-10">
                    <FormParagraph
                        bold
                        key={name}
                    >
                        {bevindingenLabels[name] ? bevindingenLabels[name] : bevindingen[name]}
                    </FormParagraph>
                </div>
                <div className="col-sm-4">
                    <FormParagraph
                        bold
                        key={nameOd}
                    >
                        {bevindingen[nameOd] ? 'OD' : null}
                    </FormParagraph>
                </div>
                <div className="col-sm-4">
                    <FormParagraph
                        bold
                        key={nameOs}
                    >
                        {bevindingen[nameOs] ? 'OS' : null}
                    </FormParagraph>
                </div>
            </div>
        ) : null;
    }

    function isWerkdiagnoseVisible() {
        const werkdiagnose: ITocV1DiagnosticReportCustomControlDataWerkdiagnose =
            props.diagnosticReport.customControlData.werkdiagnose;

        return (
            werkdiagnose.cataract ||
            werkdiagnose.macula_degeneratie ||
            werkdiagnose.glaucoom ||
            werkdiagnose.retinopathie ||
            werkdiagnose.droge_ogen
        );
    }

    function werkdiagnoseOption<K extends keyof ITocV1DiagnosticReportCustomControlDataWerkdiagnose>(
        name: K,
        suboptions?: K[]
    ) {
        const werkdiagnose: ITocV1DiagnosticReportCustomControlDataWerkdiagnose =
            props.diagnosticReport.customControlData.werkdiagnose;

        return werkdiagnose[name] ? (
            <FlexColumns sm={6}>
                <FormParagraph
                    bold
                    key={name}
                >
                    {tocV1DiagnosticReportWerkdiagnoseLabels[name]}
                </FormParagraph>
                {suboptions &&
                    suboptions
                        .filter((option) => werkdiagnose[option])
                        .map((option) => (
                            <FormParagraph
                                bold
                                key={option}
                            >
                                {tocV1DiagnosticReportWerkdiagnoseLabels[option]}
                            </FormParagraph>
                        ))}
            </FlexColumns>
        ) : null;
    }
}
